import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import styled, { StyledComponent } from 'styled-components';

import { Translate } from 'react-localize-redux';

// Store
import { CloseMenu } from '@webapp/store/ui/action-creators';
import { UpdateField } from '@webapp/store/release/action-creators';
// TYPES
import { Option } from '@webapp/types/common/option';
import { ContactInfo } from '@webapp/types/common/contact-info';
import { ArtistSearchResult } from '@services/artist/types/artist-search-result';
import { ReleaseArtist } from '@common/types/release/release-artist';
import SimpleArtist from '@common/types/artist/simple-artist';
import { TrackCollaborator } from '@common/types/release/track-collaborator';
import { ReleaseArtistRole } from '@common/types/release/release-artist-role';
import { TrackContributor } from '@common/types/release/track-contributor';

// Types
import { FugaContributorRole } from '@services/publisher/types/FugaContributorRole';

// Components
import Menu from '@components/menu';
import BoxHeader from '@components/layout/box-header';
import Typography from '@components/basics/typography';
import CloseIcon from '@components/basics/icons/close';
import DotsCircleIcon from '@components/basics/icons/dots-circle';

// Controllers
import ArtistInviteSearchCtrl from '@webapp/controllers/artist/artist-invite-search';
import {
  GenericDropdownBuilder,
  DropdownCtrlProps,
} from '@webapp/controllers/form/dropdown';

// Custom Types
type AuthorType = TrackCollaborator | TrackContributor;
type AuthorRole = string | ReleaseArtistRole | FugaContributorRole;

interface AuthorsCtrlProps<T extends AuthorType, U extends AuthorRole> {
  id: string;
  className?: string;
  children?: React.ReactNode;
  hint?: React.ReactNode;
  artists: T[];
  activeArtist?: T;
  assetId?: string;
  allArtists?: T[];
  roleList: Option<U>[];
  label: React.ReactNode;
  text?: React.ReactNode;
  songName: string;
  defaultRole: U;
  disabledIds?: string[];
  noNumber?: boolean;
  requiredRole?: string;
  disableDropdown?: boolean;
  handleEmailOrPhoneChange: (newValue: ContactInfo) => void;
  onConfirmName: () => void;
  contact?: ContactInfo;
  showError: React.ReactNode;
  setShowError: React.Dispatch<React.SetStateAction<React.ReactNode>>;
  onRemoveAuthor: (artists: ReleaseArtist[]) => UpdateField<'artists'>;
  onNewAuthors: (newAuthors: ArtistSearchResult) => void;
  dropdown: StyledComponent<
    (props: DropdownCtrlProps<U | string>) => JSX.Element,
    any,
    {},
    never
  >;
  type: 'collaborator' | 'credits';
  setRole: React.Dispatch<React.SetStateAction<string>>;
  setName?: React.Dispatch<React.SetStateAction<string>>;
  closeMenu: () => CloseMenu;
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onRemoveAuthor: (artists: ReleaseArtist[]) =>
    dispatch(new UpdateField<'artists'>('artists', artists)),
  closeMenu: () => dispatch(new CloseMenu()),
});

export const AuthorsCtrl = <T extends AuthorType, U extends AuthorRole>(
  props: AuthorsCtrlProps<T, U>
) => {
  const DropdownCtrl = props.dropdown;

  const { artists, defaultRole, allArtists } = props;
  const [updates, setUpdates] = React.useState(artists);
  const [newArtist, setNewArtist] = React.useState<T>();

  React.useEffect(() => {
    if (allArtists) {
      setUpdates(allArtists);
    }
  }, [allArtists]);

  const resetArtist = React.useCallback(() => {
    const newUpdate = {
      name: '',
      role: defaultRole as U,
    } as unknown as T;
    setNewArtist(newUpdate);
  }, [defaultRole]);

  React.useEffect(() => {
    resetArtist();
  }, [artists, resetArtist]);

  const removeAuthor = (deleteItem: Partial<T>) => {
    const newUpdates = updates.filter(
      (option) => option?.id !== deleteItem?.id
    );

    setUpdates(newUpdates);
    const validOptions = newUpdates.filter((option) => isValid(option));

    props.onRemoveAuthor(validOptions as ReleaseArtist[]);
  };

  const isValid = (option: Partial<T>) => {
    return !!(option.id && option.role);
  };

  const handleEdit = <F,>(i: number, value: F, field?: string) => {
    const update = field ? { [field]: value } : value;

    const newUpdates = updates.map((a, j) => {
      if (i === j) {
        return {
          ...a,
          ...update,
        };
      }
      return { ...a };
    });

    setUpdates(newUpdates);
  };
  const handleEditNew = <F,>(value: F, field?: string, final?: boolean) => {
    const updatedValue = field ? { [field]: value } : value;
    const update: T = {
      ...newArtist,
      ...updatedValue,
    } as T;

    setNewArtist(update);
    if (final) {
      const newUpdates = [update, ...updates];
      setUpdates(newUpdates);
      if (isValid(update)) {
        resetArtist();
      }
    }
  };

  const handleArtistSelection = (
    i: number,
    value: SimpleArtist | ArtistSearchResult
  ) => {
    if (props.artists.find((a) => a.id === value.id)) {
      return;
    }
    handleEdit<SimpleArtist | ArtistSearchResult>(i, value);
  };
  const handleArtistSelectionNew = (value: ArtistSearchResult) => {
    props.onNewAuthors(value);
  };

  const handleConfirmName = () => {
    props.onConfirmName();
    resetArtist();
  };

  const isDisabled = (value: Partial<T>) => {
    if (value.id === props.activeArtist?.id) return true;
    if (props.disabledIds) {
      if (props.disabledIds.find((i) => i === value.id)) {
        return props.requiredRole ? value.role === props.requiredRole : true;
      }
    }

    if (props.requiredRole) {
      return (
        value.role === props.requiredRole &&
        updates.filter((a) => a.role === props.requiredRole).length <= 1
      );
    }

    return false;
  };

  return (
    <div className={props.className}>
      <BoxHeader title={props.label} text={props.text} />
      <Wrapper>
        <ArtistInviteSearchCtrl
          id={props.id}
          className="invite-input"
          label={
            <Translate id="RELEASE.NAMES_GENRES.MAIN_ARTISTS.LABEL.INPUT.NEW" />
          }
          value={newArtist}
          currentValues={props.allArtists ? props.allArtists : updates}
          role={newArtist?.role ?? ''}
          type={props.type}
          songName={props.songName}
          handleEmailOrPhoneChange={props.handleEmailOrPhoneChange}
          contact={props.contact}
          showError={props.showError}
          setShowError={props.setShowError}
          setRole={props.setRole}
          setName={props.setName}
          noDropdown={props.disableDropdown}
          source={{
            assetType: 'TRACK',
            type:
              props.type === 'collaborator' ? 'COLLABORATOR' : 'CONTRIBUTOR',
            id: props.assetId,
          }}
          onChange={(value) => handleEditNew({ name: value, id: undefined })}
          onSelection={(artist) =>
            handleArtistSelectionNew(artist as ArtistSearchResult)
          }
          dataType="artist"
          onConfirmName={handleConfirmName}
        >
          {props.disableDropdown ? null : (
            <DropdownCtrl
              id={props.id + 'Dropdown'}
              className="input-split"
              parentRelative
              title="Role"
              options={props.roleList}
              type={props.type}
              value={newArtist?.role as U}
              customBorderRadius="0 0 4px 4px"
              onChange={(value: U | string) =>
                handleEditNew<U>(value as U, 'role')
              }
            />
          )}
        </ArtistInviteSearchCtrl>
        {props.children}
        {updates.map((option: Partial<T>, i) => (
          <ArtistInviteSearchCtrl
            className="invite-input"
            label={
              <Translate id="RELEASE.NAMES_GENRES.MAIN_ARTISTS.LABEL.INPUT" />
            }
            isActiveArtist={props.activeArtist?.id === option.id}
            key={`${props.id}-${i}`}
            value={option as SimpleArtist}
            currentValues={props.allArtists ? props.allArtists : updates}
            role={option.role as string}
            locked={true}
            type={props.type}
            songName={props.songName}
            handleEmailOrPhoneChange={props.handleEmailOrPhoneChange}
            contact={props.contact}
            showError={props.showError}
            noDropdown={props.disableDropdown}
            setShowError={props.setShowError}
            setRole={props.setRole}
            setName={props.setName}
            hasHint={updates.length > 0 && i === updates.length - 1}
            source={{
              assetType: 'TRACK',
              type:
                props.type === 'collaborator' ? 'COLLABORATOR' : 'CONTRIBUTOR',
              id: props.assetId,
            }}
            firstBigger
            decoration={
              !props.noNumber ? (
                <Typography
                  type="input-number"
                  className={`${isDisabled(option) ? '' : 'input-number'}`}
                >
                  {`${String(i + 1).padStart(2, '0')}.`}
                </Typography>
              ) : !isDisabled(option) ? (
                <Menu
                  className="menu"
                  button={<DotsCircleIcon className="open-icon" />}
                  closeButton={
                    <CloseIcon
                      onClick={props.closeMenu}
                      className="close-icon"
                    />
                  }
                  customIconHeight="24px"
                  menuId={`authors-menu-${i}`}
                >
                  <MenuContent>
                    <Typography
                      weight="700"
                      className="menu-item danger"
                      onClick={() => removeAuthor(option)}
                    >
                      <Translate id="RELEASE.NAMES_GENRES.MAIN_ARTISTS.LABEL.BUTTON.REMOVE" />
                    </Typography>
                  </MenuContent>
                </Menu>
              ) : null
            }
            onChange={(value) =>
              handleEdit(i - 1, { name: value, id: undefined })
            }
            onSelection={(value) => handleArtistSelection(i - 1, value)}
            dataType="artist"
          >
            {props.disableDropdown ? null : props.activeArtist?.id ===
              option.id ? (
              <DropdownCtrl
                className="input-split"
                title="Role"
                disabled={isDisabled(option)}
                value={'Main'}
                type={props.type}
                customBorderRadius="0 0 4px 4px"
              />
            ) : (
              <DropdownCtrl
                className="input-split"
                title="Role"
                options={props.roleList}
                disabled={isDisabled(option)}
                value={option.role as U}
                customBorderRadius="0 0 4px 4px"
                type={props.type}
                onChange={(value: U | string) =>
                  handleEdit<U>(i - 1, value as U, 'role')
                }
              />
            )}
          </ArtistInviteSearchCtrl>
        ))}
      </Wrapper>
    </div>
  );
};

export const buildAuthorsCtrl = <
  T extends AuthorType,
  U extends AuthorRole
>() => {
  const DropdownCtrl = styled(GenericDropdownBuilder<U | string>())`
    .opened {
      .container {
        z-index: 99999;
      }
    }
  `;

  return connect(
    null,
    mapDispatchToProps
  )(styled((props: Omit<AuthorsCtrlProps<T, U>, 'dropdown'>) => (
    <AuthorsCtrl dropdown={DropdownCtrl} {...props} />
  ))`
    .menu {
      .menu-action {
        z-index: 0;
      }

      .open-icon,
      .close-icon {
        width: 24px;
        height: 24px;
        cursor: pointer;
      }
    }

    .decoration-right {
      z-index: 2;
    }
  `);
};

const Wrapper = styled.div`
  display: grid;
  grid-gap: 10px;
`;

const MenuContent = styled.div`
  display: grid;
  grid-gap: 5px;
  min-width: 128px;

  .menu-item {
    cursor: pointer;
    transition: color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};

    &:hover {
      color: ${({ theme }) => theme.colors.tertiary};
    }

    &.danger {
      color: ${({ theme }) => theme.colors.accent};
      &:hover {
        color: ${({ theme }) => theme.colors.accentHover};
      }
    }
  }
`;
